/**
 * Single source of truth for the Google S2 favicon URL.
 *
 * All consumers (DomainFavicon, BrandLogo, vis-scan) must call
 * this instead of inlining the URL template. The `sz` param defaults to 64
 * (hi-DPI favicon); callers that need a different resolution pass it explicitly.
 *
 * @param domain - bare domain string, e.g. "example.com" (no protocol)
 * @param sz - pixel size hint sent to Google S2 (16 | 32 | 64 | 128). Default 64.
 */
export function googleFaviconUrl(domain: string, sz: 16 | 32 | 64 | 128 = 64): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${sz}`
}

function resolveUrl(href: string, domain: string): string {
  if (/^https?:\/\//i.test(href)) return href
  if (href.startsWith('//')) return `https:${href}`
  if (href.startsWith('/')) return `https://${domain}${href}`
  return `https://${domain}/${href}`
}

export function extractLogoUrl(html: string, domain: string): string | null {
  // 1. <link rel="apple-touch-icon"> or rel="icon" in <head>
  const headMatch = html.match(/<head[\s\S]*?<\/head>/i)
  const headSection = headMatch ? headMatch[0] : html
  const linkRe = /<link[^>]+rel=["'](?:apple-touch-icon|icon)["'][^>]*href=["']([^"']+)["'][^>]*>/gi
  let match = linkRe.exec(headSection)
  if (!match) {
    // also try href before rel
    const linkRe2 =
      /<link[^>]+href=["']([^"']+)["'][^>]*rel=["'](?:apple-touch-icon|icon)["'][^>]*>/gi
    match = linkRe2.exec(headSection)
  }
  if (match?.[1]) return resolveUrl(match[1], domain)

  // 2. <img> inside <header> or <nav> where src/class/id/alt contains "logo"
  const containerRe = /<(?:header|nav)[^>]*>([\s\S]*?)<\/(?:header|nav)>/gi
  let containerMatch: RegExpExecArray | null
  while ((containerMatch = containerRe.exec(html)) !== null) {
    const inner = containerMatch[1] ?? ''
    const imgRe = /<img([^>]+)>/gi
    let imgMatch: RegExpExecArray | null
    while ((imgMatch = imgRe.exec(inner)) !== null) {
      const attrs = imgMatch[1] ?? ''
      if (/logo/i.test(attrs)) {
        const srcMatch = attrs.match(/src=["']([^"']+)["']/)
        if (srcMatch?.[1]) return resolveUrl(srcMatch[1], domain)
      }
    }
  }

  // 3. <meta property="og:image"> — only if path contains "logo"
  const ogMatch = html.match(/<meta[^>]+property=["']og:image["'][^>]*content=["']([^"']+)["']/i)
  const ogUrl = ogMatch?.[1] ?? null
  if (ogUrl) {
    try {
      const parsed = new URL(ogUrl.startsWith('//') ? `https:${ogUrl}` : ogUrl)
      if (/logo/i.test(parsed.pathname)) return ogUrl
    } catch {
      // malformed OG URL — fall through to favicon
    }
  }

  // 4. Fallback: Google favicon
  return googleFaviconUrl(domain)
}
